/* eslint-disable max-lines-per-function */
import React from "react";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Form, Formik, FormikConfig } from "formik";
import { CardActions, CardContent, Grid, Typography, Tooltip } from "@material-ui/core";
import * as yup from "yup";
import { sortBy } from "lodash";
import { HistoryBackButton } from "components/buttons";
import { Button } from "components/haven-design/buttons";
import { InputWithErrorLabel } from "components/haven-design/form/inputs";
import { SwitchWithErrorLabel } from "components/haven-design/form/checkbox";
import { getFormDefinitions_formDefinitions as FormDraft, FormType, GetProperties, LoadForm, LoadFormVariables, getFormQuestions, getFormQuestions_formQuestions } from "types/gql-types";
import { MaterialFormikSelect as SelectWithLabel } from "components/haven-design/form/select";
import { LoadingSpinner } from "components/graphql-views";
import { useSnackbar } from "components/haven-design/snackbar";
import { TOGGLE_FORM_OPEN, TOGGLE_FORM_DISPLAY } from "pages/dashboard/settings/property/lists/list-settings";
import { GET_FORM_QUESTIONS } from "../forms-questions";
import TOMLValidator, { validateContent } from "./toml-validator";
import { LOAD_FORM } from "./index";

export type FormDefinition = Omit<FormDraft, "__typename" | "id" | "property"> & { content: string, id: string | null | undefined };

interface Props {
  initialData: FormDefinition;
  formType: "update" | "new";
  handleSubmit: FormikConfig<FormDefinition>["onSubmit"];
}

const formType = Object.keys(FormType);

export const defaultFormSchema = yup.object().shape({
  __typename: yup.string().strip(true),

  id: yup.string().strip(true),

  propertyId: yup
    .string()
    .trim()
    .required(),

  name: yup
    .string()
    .trim()
    .required(),

  enabled: yup
    .boolean()
    .required(),

  createInterest: yup
    .boolean()
    .required(),

  skipSignature: yup.boolean().default(false),
  renderParagraphs: yup.boolean().default(false),

  ageRequirement: yup.number().nullable().required().default(18),
  ageRequirementOrDisabilityOverride: yup.boolean().nullable().default(false),

  content: yup
    .string()
    .trim()
    .required(),

  formType: yup.string().oneOf(formType).required(),

  formId: yup.string().nullable(),
  form: yup.object().shape({
    id: yup.string(),
    display: yup.boolean(),
    open: yup.boolean(),
  }).nullable(),
});

const GET_PROPERTIES = gql`
  query GetProperties {
    properties {
      id
      name
    }
  }
`;

const publishTooltip = (values:FormDefinition):string => {
  if (!values.id) return "Form must be saved";
  if (!values.enabled) return "Form must be enabled";

  return "Publish form";
};

const DefinitionsForm: React.FC<Props> = (props) => {
  const [loadForm] = useMutation<LoadForm, LoadFormVariables>(LOAD_FORM);
  const { loading, error, data } = useQuery<GetProperties>(GET_PROPERTIES);
  const { loading: fQLoading, error: fQError, data: fQData } =
  useQuery<getFormQuestions>(GET_FORM_QUESTIONS, { fetchPolicy: "no-cache" });
  const [existingQuestions, setQuestions] = React.useState<Record<string, string>>({});
  const { openSnackbar } = useSnackbar();

  const [toggleFormOpen, { loading: formOpenLoading }] = useMutation(TOGGLE_FORM_OPEN);
  const [toggleFormDisplay, { loading: formDisplayLoading }] = useMutation(TOGGLE_FORM_DISPLAY);

  React.useEffect(() => {
    if (!fQLoading && !fQError && fQData?.formQuestions) {
      const questionsMap = (fQData.formQuestions as getFormQuestions_formQuestions[])
        .reduce((acc, question) =>
          ({ ...acc, [question.name]: question?.valueType }), {});
      setQuestions(questionsMap);
    }
  }, [fQData, fQLoading]);

  if (loading) return <LoadingSpinner />;
  if (error || !data || !data?.properties) return <span>Server Error</span>;

  const properties = sortBy(data?.properties || [], [(property) => property.name]);

  return (
    <Formik
      initialValues={props.initialData}
      onSubmit={props.handleSubmit}
      validationSchema={defaultFormSchema}>
      {({ values, setFieldValue, submitForm }) => (
        <Form>
          <CardContent>
            <Grid container spacing={1}>

              <Grid item xs={12} md={6} lg={5}>
                <InputWithErrorLabel label="Name *" name="name" type="text" />
              </Grid>

              <Grid item xs={12} md={6} lg={5}>
                <SelectWithLabel title="Property" name="propertyId"
                  options={[
                    { label: "Select", value: "" },
                    ...properties.map(({ id, name }) => ({ label: name, value: id })),
                  ]} />
              </Grid>

              <Grid item xs={12} md={6} lg={5}>
                <SelectWithLabel title="Form type" name="formType"
                  options={[
                    { label: "Select", value: "" },
                    ...formType.map((formTypeId) => ({ label: formTypeId, value: formTypeId })),
                  ]} />
              </Grid>

              <Grid item xs={12} md={6}>
                <SwitchWithErrorLabel label="Is form safe to be applied to?" name="enabled" />
              </Grid>

              <Grid item xs={12} md={6}>
                <SwitchWithErrorLabel label="Allowed to create an interest?" name="createInterest" />
              </Grid>

              <Grid item container xs={12} spacing={1}>
                <Grid item xs={12} md={6}>
                  <SwitchWithErrorLabel
                    label="Disable Signature?"
                    name="skipSignature"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SwitchWithErrorLabel
                    label="Render Paragraphs on the PDF?"
                    name="renderParagraphs"
                  />
                </Grid>
              </Grid>

              <Grid item container xs={12} spacing={1}>
                <Grid item xs={12} md={6}>
                  <InputWithErrorLabel
                    label="Minimum Age Requirement *"
                    name="ageRequirement"
                    type="number"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <SwitchWithErrorLabel
                    label="Disability Overrides Minimum Age Requirement? *"
                    name="ageRequirementOrDisabilityOverride"
                  />
                </Grid>
              </Grid>

            </Grid>

            <InputWithErrorLabel label="Content *" name="content" type="text" multiline maxRows={18} />
          </CardContent>
          <CardActions>
            <Grid container spacing={1}>
              <Grid item>
                <HistoryBackButton size="L" />
              </Grid>
              <Grid item>
                <Button color="primary" size="L" type="submit">
                  Save
                </Button>
              </Grid>
              <Grid item>
                <TOMLValidator content={values.content} existingQuestions={existingQuestions} />
              </Grid>
              <Grid item>
                <Tooltip title={publishTooltip(values)}>
                  <div><Button size="L" color="primary" disabled={!values.id || !values.enabled} onClick={() => {
                    if (validateContent(values.content, existingQuestions).length != 0) {
                      openSnackbar({ message: "Please check TOML validity", status: "error" });
                      return;
                    }

                    submitForm()
                      .then(() => loadForm({ variables: { id: values.id ?? "" } }))
                      .then(() => openSnackbar({ message: "Published", status: "success" }));
                  }}>
                  Save and Publish
                  </Button></div>
                </Tooltip>
              </Grid>
            </Grid>
          </CardActions>
          <CardContent>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <Typography variant="h6">Form Settings</Typography>
              </Grid>

              <Grid item>
                {(props.formType === "update") ? (
                  <SwitchWithErrorLabel
                    label="Form is open?"
                    name="form.open"
                    disabled={!values.form}
                    checked={values.form?.open}
                    onChange={() => {
                      if (!formOpenLoading) {
                        toggleFormOpen({ variables: { id: values.formId } })
                          .finally(() => {
                            openSnackbar({ message: "Form Open status updated", status: "success" });
                            setFieldValue("form.open", !values.form?.open);
                          });
                      }
                    }}
                  />
                ) : (
                  <SwitchWithErrorLabel
                    label="Form is open?"
                    name="form.open"
                    disabled={!values.form}
                  />
                )}
              </Grid>
              {(props.formType === "update") && (
                <Grid item>
                  <SwitchWithErrorLabel
                    label="Form is showcased?"
                    name="form.display"
                    disabled={!values.form}
                    checked={values.form?.display}
                    onChange={() => {
                      if (!formDisplayLoading) {
                        toggleFormDisplay({ variables: { id: values.formId } })
                          .finally(() => {
                            openSnackbar({ message: "Form display status updated", status: "success" });
                            setFieldValue("form.display", !values.form?.display);
                          });
                      }
                    }}
                  />
                </Grid>)}
              {values.formId && (
                <Grid item>
                  {`${location.origin}/form/${values.formId}`}
                </Grid>)}
            </Grid>
          </CardContent>
        </Form>
      )}
    </Formik>
  );
};

export default DefinitionsForm;
